/**
 * Login Page.
 *
 * Email/password authentication form with a polished split-screen layout.
 * Left panel: branded hero. Right panel: login form.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLogin } from "@/features/auth/api";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";

function LoginPage() {
  const navigate = useNavigate();
  const loginMutation = useLogin();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      await loginMutation.mutateAsync({ email, password });
      navigate("/", { replace: true });
    } catch {
      setError("Invalid email or password. Please try again.");
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left Panel — Cinematic Gradient Mesh Hero */}
      <div
        className="hidden w-1/2 lg:flex lg:flex-col lg:justify-between lg:p-12 relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, oklch(8% 0.04 285) 0%, oklch(12% 0.05 290) 40%, oklch(9% 0.04 310) 70%, oklch(7% 0.03 260) 100%)",
        }}
      >
        {/* Animated decorative gradient orbs */}
        <div className="absolute top-[-15%] right-[-8%] h-[550px] w-[550px] rounded-full bg-brand-500/20 blur-[140px] animate-float" />
        <div
          className="absolute bottom-[-10%] left-[-10%] h-[450px] w-[450px] rounded-full bg-accent-500/12 blur-[120px] animate-float"
          style={{ animationDelay: "-3s" }}
        />
        <div className="absolute top-[40%] left-[30%] h-[300px] w-[300px] rounded-full bg-brand-400/8 blur-[100px] animate-breathe" />
        {/* Subtle noise texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />

        <div className="flex items-center gap-3 relative z-10">
          <img
            src="/logo.png"
            alt="TradeRoute"
            className="h-12 w-12 rounded-[var(--radius-xl)] ring-1 ring-white/10"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <span className="text-lg font-semibold text-brand-300 tracking-tight">
            TradeRoute
          </span>
        </div>

        <div className="relative z-10">
          <h2 className="text-4xl font-bold text-white leading-[1.15] tracking-tight">
            Your reseller business,
            <br />
            <span className="bg-gradient-to-r from-brand-300 via-brand-400 to-accent-400 bg-clip-text text-transparent">
              all in one place.
            </span>
          </h2>
          <p className="mt-5 max-w-md text-[15px] leading-relaxed text-slate-400/90">
            Track every asset from acquisition to sale. Manage procurement,
            refurbishment, sales, and financing with complete cost visibility
            and margin intelligence.
          </p>
        </div>

        <p className="text-xs text-slate-600/80 relative z-10">
          TradeRoute v2 &mdash; Reseller Business Platform
        </p>
      </div>

      {/* Right Panel — Login Form (Gradient mesh background) */}
      <div
        className="flex w-full items-center justify-center px-6 lg:w-1/2"
        style={{
          background: `linear-gradient(135deg, var(--color-bg-gradient-from), var(--color-bg-gradient-via), var(--color-bg-gradient-to))`,
        }}
      >
        <div className="w-full max-w-sm">
          {/* Mobile Brand (shown only on small screens) */}
          <div className="mb-8 text-center lg:hidden">
            <img
              src="/logo.png"
              alt="TradeRoute"
              className="mx-auto mb-3 h-14 w-14 rounded-[var(--radius-xl)]"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <h1 className="text-xl font-bold text-[var(--text-primary)]">
              TradeRoute
            </h1>
          </div>

          <div className="hidden lg:block lg:mb-8">
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">
              Welcome back
            </h1>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Sign in to your account
            </p>
          </div>

          {/* Form Card — Frosted Glass */}
          <div className="rounded-[var(--radius-2xl)] glass glass-glow p-8 animate-fade-in">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="rounded-[var(--radius-lg)] bg-[var(--fill-danger)] p-3 text-sm text-[var(--text-danger)] dark:bg-red-900/20 dark:text-red-300">
                  {error}
                </div>
              )}

              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
              />

              <div>
                <Input
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <div className="mt-1.5 text-right">
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium text-brand-600 hover:text-brand-700"
                  >
                    Forgot your password?
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                isLoading={loginMutation.isPending}
              >
                Sign In
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-sm text-[var(--text-secondary)]">
            Don&apos;t have an account?{" "}
            <a
              href="/register"
              className="font-medium text-brand-600 hover:text-brand-700"
            >
              Create one
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
